import { differenceInDays, parseISO } from "date-fns";
import { Check, ExternalLink, Link2, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { authClient } from "@/lib/auth";
import { useSubscription } from "@/queries/subscription-queries";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { PaddlePaymentDialog } from "./PaddlePaymentDialog";

function TrialAlert({ title, description }: { title: string; description: React.ReactNode }) {
    return (
        <Alert className="bg-slate-300/80 text-foreground">
            <ShieldAlert />
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription className="text-foreground text-sm">{description}</AlertDescription>
        </Alert>
    );
}

export default function UpgradeSubscription({ isCanceled }: { isCanceled?: boolean }) {
    const [_isNavigating, _setIsNavigating] = useState(false);
    const { data: subscriptionData } = useSubscription();
    const { formatDate } = useDateFormatter();

    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

    const handleCheckoutPaddle = async () => {
        setPaymentDialogOpen(true);
    };

    const trialDaysLeft = subscriptionData?.trialEndsAt
        ? differenceInDays(subscriptionData?.trialEndsAt, new Date())
        : 0;
    const effectiveAt =
        isCanceled && subscriptionData?.paddleSubscription?.scheduledChange?.effectiveAt
            ? parseISO(subscriptionData!.paddleSubscription!.scheduledChange!.effectiveAt)
            : null;

    const handleLogout = async () => {
        await authClient.signOut({
            fetchOptions: {
                onSuccess: () => {
                    window.location.href = "/login";
                },
            },
        });
    };

    return (
        <>
            {trialDaysLeft <= 0 && !isCanceled && (
                <TrialAlert
                    title="Your free trial is over."
                    description={
                        <div>
                            Upgrade today to continue collaborating. Questions?{" "}
                            <a
                                className="inline-block hover:underline underline-offset-2"
                                href="mailto:support@orsane.app"
                                target="_blank"
                                rel="noopener"
                            >
                                Contact us <Link2 className="size-3.5 inline-block ml-0.5" />
                            </a>
                        </div>
                    }
                />
            )}
            {isCanceled && (
                <TrialAlert
                    title="Your subscription has been canceled."
                    description={`You will lose access to your tasks on ${effectiveAt ? formatDate(effectiveAt) : "the next renewal date"}.`}
                />
            )}

            {!isCanceled && trialDaysLeft > 0 && (
                <TrialAlert
                    title={`${trialDaysLeft} ${trialDaysLeft === 1 ? "day" : "days"} left on your free trial.`}
                    description="Upgrade today to continue collaborating."
                />
            )}
            <div className="border bg-white rounded-lg p-4 flex justify-between">
                <div>
                    <div className="text-md font-semibold">Pro</div>
                    <div>$15 per user/month</div>
                    <div className="mt-6">
                        <Button type="button" onClick={handleCheckoutPaddle}>
                            Upgrade plan
                            <ExternalLink />
                        </Button>
                    </div>
                </div>
                <div>
                    <div className="*:flex *:items-center *:gap-2 [&_svg]:size-4 grid grid-cols-2 gap-x-6 items-start gap-y-1">
                        <div>
                            <Check className="" />
                            Unlimited task lists
                        </div>
                        <div>
                            <Check className="" />
                            Unlimited tasks
                        </div>
                        <div>
                            <Check className="" />
                            Unlimited members
                        </div>
                        <div>
                            <Check className="" />
                            Admin roles
                        </div>
                        <div>
                            <Check className="" />
                            Custom attributes
                        </div>
                        <div>
                            <Check className="" />
                            Collaboration features
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-end">
                <img src="/payment-logos/payment-logos.svg" className="h-5" alt="Payment logos" />
                <p className="mt-2 text-xs text-gray-500">Payment processed securely by Paddle.</p>
                {trialDaysLeft <= 0 && !isCanceled && (
                    <div className="text-xs">
                        <Button variant="link" className="text-gray-600 p-0 cursor-pointer" onClick={handleLogout}>
                            Logout
                        </Button>
                    </div>
                )}
            </div>
            <PaddlePaymentDialog open={paymentDialogOpen} onClose={(opened) => setPaymentDialogOpen(opened)} />
        </>
    );
}
